import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import NavigationTabs from './NavigationTabs';
import NavigationMore from './NavigationMore';

const NavigationTabsWithMore = ({
  active,
  options,
  tabLimit = null,
  moreButtonOffset = 88,
  className,
  navigationTabsProps,
  navigationMoreProps,
  onChange,
}) => {
  const tabsRef = useRef(null);
  const containerRef = useRef(null);

  const [splitIndex, setSplitIndex] = useState(tabLimit);

  const tabOptions = options.length > splitIndex ? options.slice(0, splitIndex) : options;

  const moreOptions = options.length > splitIndex ? options.slice(splitIndex) : [];

  useEffect(() => {
    const calculateSplitIndex = () => {
      const tabsWidth = tabsRef.current?.offsetWidth || 0;
      const tabsScrollWidth = tabsRef.current?.scrollWidth || 0;
      const containerWidth = containerRef.current?.offsetWidth || 0;

      if (tabsScrollWidth > tabsWidth) {
        const rightOffset = containerWidth === tabsWidth ? moreButtonOffset : 0;
        const tabElements = tabsRef.current.querySelectorAll('[data-value]');

        const overflowIndex = Array.from(tabElements).findIndex((tab) => {
          const { width } = tab.getBoundingClientRect();
          const elementRightPosition = tab.offsetLeft + width;

          return (elementRightPosition + rightOffset) > tabsWidth;
        });

        setSplitIndex(overflowIndex === -1 ? tabElements.length : overflowIndex);
      }
    };

    const observer = new ResizeObserver(() => {
      calculateSplitIndex();
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={cx('navigation-tabs-with-more', className)}>
      <NavigationTabs
        ref={tabsRef}
        active={active}
        options={tabOptions}
        {...navigationTabsProps}
        onChange={onChange}
      />
      {moreOptions.length > 0 && (
        <NavigationMore
          selected={active}
          options={moreOptions}
          {...navigationMoreProps}
          onChange={onChange}
        />
      )}
    </div>
  );
};

NavigationTabsWithMore.propTypes = {
  active: T.string,
  options: T.arrayOf(T.shape({
    label: T.string.isRequired,
    value: T.string.isRequired,
    href: T.string,
    disabled: T.bool,
    notAllowed: T.bool,
    tooltipContent: T.node,
  })).isRequired,
  tabLimit: T.number,
  moreButtonOffset: T.number,
  className: T.string,
  navigationTabsProps: T.object,
  navigationMoreProps: T.object,
  onChange: T.func.isRequired,
};

export default NavigationTabsWithMore;
